import { Page } from "@/pages/explorer/types";
import { TaskListResponse } from "@/pages/query/types";
import { useAppState } from "@/modules/app/useAppContext";
import { formatCost, formatMilliseconds } from "@/helpers/utils";
import { Stack } from "@altimateai/lego";
import { DataTable } from "@/components/tables";
import { Paginate } from "@/components/paginate";
import { FilterType, TableHeaderType } from "@/components/tables/types";
import { useMemo } from "react";
import useFiltersBySearchParams from "@/helpers/useFiltersBySearchParams";
import { FilterApplicableFor } from "@/components/tags/types";
import { useTablePagination } from "@/components/tables/hooks/useTablePagination";

const SORT_SEARCH_KEY = "sortAttribute";
const SORT_ORDER_SEARCH_KEY = "sortOrder";

const FILTER_PORTAL_ID = "tasks-filter-container";
const SAVED_FILTERS_PORTAL_ID = "tasks-saved-filters-container";

const formatSuccessRate = (item: TaskListResponse): string => {
  if (item.run_count <= 0) {
    return "—";
  }
  const rate = (item.success_count / item.run_count) * 100;
  // Avoid trailing ".0" while keeping one decimal for partial rates
  const formatted = Number.isInteger(rate) ? rate.toString() : rate.toFixed(1);
  return `${formatted}%`;
};

export const TasksListTable = ({
  tableData,
  filters = { warehouses: [], databases: [], schemas: [] },
  isLoading = false,
  applicableFor = FilterApplicableFor.TASKS,
}: {
  tableData: Page<TaskListResponse> | undefined;
  filters?: { warehouses: string[]; databases: string[]; schemas: string[] };
  isLoading?: boolean;
  applicableFor?: FilterApplicableFor;
}) => {
  const { currency } = useAppState();
  const { searchParams, updateSearchParamsByKey } = useFiltersBySearchParams();
  const { page, pageSize, setPage, setPageSize, totalItems, totalPages } = useTablePagination();
  const warehouseFilter = useMemo(() => searchParams.getAll("warehouses") || [], [searchParams]);
  const databaseFilter = useMemo(() => searchParams.getAll("databases") || [], [searchParams]);
  const schemaFilter = useMemo(() => searchParams.getAll("schemas") || [], [searchParams]);

  const tableHeaders: TableHeaderType<TaskListResponse>[] = useMemo(
    () => [
      {
        id: 1,
        label: "Task Name",
        uid: "name",
        width: "25%",
        render: (item: TaskListResponse) => (
          <span className="al-text-brand al-break-all condense-text cursor-pointer">
            {item.name}
          </span>
        ),
      },
      {
        id: 2,
        label: "Database",
        uid: "databases",
        width: "15%",
        render: (item: TaskListResponse) => (
          <div className="al-break-all">{item.database_name || "—"}</div>
        ),
        filter: {
          id: "databases",
          label: "Databases",
          searchKey: "databases",
          filterType: FilterType.DROPDOWN,
          value: databaseFilter,
          options: filters.databases.map(db => ({ label: db, value: db })),
          onChange: (value: string[]) => {
            updateSearchParamsByKey({ databases: value, page: "0" });
          },
        },
      },
      {
        id: 3,
        label: "Schema",
        uid: "schemas",
        width: "13%",
        render: (item: TaskListResponse) => (
          <div className="al-break-all">{item.schema_name || "—"}</div>
        ),
        filter: {
          id: "schemas",
          label: "Schemas",
          searchKey: "schemas",
          filterType: FilterType.DROPDOWN,
          value: schemaFilter,
          options: filters.schemas.map(schema => ({ label: schema, value: schema })),
          onChange: (value: string[]) => {
            updateSearchParamsByKey({ schemas: value, page: "0" });
          },
        },
      },
      {
        id: 4,
        label: "Warehouse",
        uid: "warehouses",
        width: "15%",
        render: (item: TaskListResponse) => (
          <div className="al-break-all">{item.warehouse_name || "—"}</div>
        ),
        filter: {
          id: "warehouses",
          label: "Warehouses",
          searchKey: "warehouses",
          filterType: FilterType.DROPDOWN,
          value: warehouseFilter,
          options: filters.warehouses.map(wh => ({ label: wh, value: wh })),
          onChange: (value: string[]) => {
            updateSearchParamsByKey({ warehouses: value, page: "0" });
          },
        },
      },
      {
        id: 5,
        label: "Runs",
        uid: "run_count",
        width: "8%",
        render: (item: TaskListResponse) => <div>{item.run_count}</div>,
        sort: {
          sortSearchKey: SORT_SEARCH_KEY,
          sortOrderSearchKey: SORT_ORDER_SEARCH_KEY,
          sortParamValue: "run_count",
        },
      },
      {
        id: 6,
        label: "Success rate",
        uid: "success_rate",
        width: "8%",
        render: (item: TaskListResponse) => <div>{formatSuccessRate(item)}</div>,
      },
      {
        id: 7,
        label: "Execution Time",
        uid: "execution_time_ms",
        render: (item: TaskListResponse) => <div>{formatMilliseconds(item.execution_time_ms)}</div>,
        sort: {
          sortSearchKey: SORT_SEARCH_KEY,
          sortOrderSearchKey: SORT_ORDER_SEARCH_KEY,
          sortParamValue: "execution_time_ms",
        },
      },
      {
        id: 8,
        label: "Total Cost",
        uid: "cost",
        render: (item: TaskListResponse) => <div>{formatCost(item.cost, currency)}</div>,
        sort: {
          sortSearchKey: SORT_SEARCH_KEY,
          sortOrderSearchKey: SORT_ORDER_SEARCH_KEY,
          sortParamValue: "cost",
        },
      },
    ],
    [
      currency,
      filters.warehouses,
      filters.databases,
      filters.schemas,
      updateSearchParamsByKey,
      warehouseFilter,
      databaseFilter,
      schemaFilter,
    ]
  );

  return (
    <Stack direction={"column"} gap={"lg"}>
      <Stack direction="row" align="center" gap="sm" className="al-flex-wrap">
        <div id={FILTER_PORTAL_ID} />
        <div id={SAVED_FILTERS_PORTAL_ID} />
      </Stack>
      <DataTable
        isLoading={isLoading}
        header={tableHeaders}
        data={tableData ?? { items: [] }}
        idKey={(item: TaskListResponse) => `${item.database_name}.${item.schema_name}.${item.name}`}
        getRowUrl={(item: TaskListResponse) =>
          `/query/tasks/runs/${encodeURIComponent(item.database_name)}/${encodeURIComponent(
            item.schema_name
          )}/${encodeURIComponent(item.name)}`
        }
        defaultSort={{ sortParamValue: "cost", order: "desc" }}
        applicableFor={applicableFor}
        portals={{ filter: FILTER_PORTAL_ID, savedFilters: SAVED_FILTERS_PORTAL_ID }}
      />
      <Paginate
        isLoading={isLoading}
        itemCount={totalItems || 0}
        numPages={totalPages || 0}
        page={page}
        pageSize={pageSize}
        onPageClick={setPage}
        onPageSizeChange={setPageSize}
        showPageSize
      />
    </Stack>
  );
};
